import { ConsiderationCriticalityBadge } from '@/components/badges/ConsiderationCriticalityBadge';
import { ConsiderationStatusBadge } from '@/components/badges/ConsiderationStatusBadge';
import { SubscriptionStatusBadge } from '@/components/badges/SubscriptionStatusBadge';
import { useRowClick } from '@/lib/navigation';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import {
    considerationsForHomeState,
    considerationTypesState,
    homeDocumentState,
    homeHqlCatalogState,
    homeTaskSubscriptionsListState,
    taskTypesByConsiderationTypeState,
} from '@/state';
import { spacesState } from '@/state/space.state';
import { useLoadableAtomValue } from '@/state/loadableHooks';
import logger from '@/util/logger';
import { ConsiderationDao, createId } from '@rock/db-admin';
import { dateToLocalDate, isActNowCriticality } from '@rock/util';
import type {
    Consideration,
    ConsiderationId,
    ConsiderationType,
    EquipmentId,
    HomeId,
    Space,
    SpaceId,
} from '@rock/types';
import { useStore } from 'jotai';
import { orderBy } from 'lodash';
import type { FC } from 'react';
import { useCallback, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useSetSearchParam } from '@/lib/searchParams';
import { toast } from 'sonner';
import { offeredCoverage } from './offeredCoverage';

const log = logger('HomeConsiderationsTab');
const considerationDao = new ConsiderationDao();

export const HomeConsiderationsTab: FC<{ homeId: HomeId }> = ({ homeId }) => {
    const [considerationTypes, considerationTypesLoadable] = useLoadableAtomValue(considerationTypesState, []);
    const [spaces] = useLoadableAtomValue(spacesState, []);
    const [searchParams] = useSearchParams();
    const setSearchParam = useSetSearchParam();
    const onlyShowOffered = searchParams.get('offered') !== 'all';
    const coverageToOffer = useCoverageToOffer();

    // ONE home-level subscription each for considerations and task-subscriptions, grouped by type
    // client-side — previously every row opened its own cross-home collection-group listener.
    const [homeConsiderations] = useLoadableAtomValue(
        useMemo(() => considerationsForHomeState(homeId), [homeId]),
        [],
    );
    // One row per type, and a withdrawn one never wins: the query has no ordering, so a row the app
    // took back could otherwise shadow a live one in the status column and hide the Offer button
    // behind it.
    const considerationByTypeId = useMemo(() => {
        const map = new Map<string, Consideration>();
        for (const consideration of homeConsiderations) {
            const held = map.get(consideration.typeId);
            if (held && held.status !== 'withdrawn' && consideration.status === 'withdrawn') continue;
            map.set(consideration.typeId, consideration);
        }
        return map;
    }, [homeConsiderations]);
    const [homeSubscriptions] = useLoadableAtomValue(
        useMemo(() => homeTaskSubscriptionsListState(homeId), [homeId]),
        [],
    );
    const subscribedTypeIds = useMemo(() => {
        const ids = new Set<string>();
        for (const subscription of homeSubscriptions) {
            if (subscription.considerationTypeId) {
                ids.add(subscription.considerationTypeId);
            }
        }
        return ids;
    }, [homeSubscriptions]);

    const isLoading = considerationTypesLoadable.state === 'loading';
    const hasError = considerationTypesLoadable.state === 'hasError';

    const spaceMap = useMemo(() => {
        const map = new Map<SpaceId, Space>();
        for (const space of spaces ?? []) {
            map.set(space.id, space);
        }
        return map;
    }, [spaces]);

    const sortedTypes = useMemo(
        () =>
            orderBy(considerationTypes, [
                (t) => (t.spaceId ? (spaceMap.get(t.spaceId)?.name.toLowerCase() ?? t.spaceId.toLowerCase()) : ''),
                (t) => t.title.toLowerCase(),
            ]),
        [considerationTypes, spaceMap],
    );

    return (
        <div className="space-y-6">
            <Card>
                <CardHeader>
                    <div className="flex items-start justify-between gap-4">
                        <div>
                            <CardTitle>Consideration Types</CardTitle>
                            <CardDescription>
                                View which consideration types apply to this home and their status.
                            </CardDescription>
                        </div>
                        <div className="flex items-center gap-2">
                            <Switch
                                id="only-show-offered"
                                checked={onlyShowOffered}
                                onCheckedChange={(checked) => setSearchParam('offered', checked ? undefined : 'all')}
                            />
                            <Label htmlFor="only-show-offered" className="text-sm font-normal cursor-pointer">
                                Only Show Offered
                            </Label>
                        </div>
                    </div>
                </CardHeader>
                <CardContent className="overflow-x-auto">
                    {isLoading ? (
                        <div className="text-muted-foreground">Loading consideration types…</div>
                    ) : hasError ? (
                        <div className="text-destructive">
                            Error loading consideration types:{' '}
                            {considerationTypesLoadable.error instanceof Error
                                ? considerationTypesLoadable.error.message
                                : `${considerationTypesLoadable.error}`}
                        </div>
                    ) : sortedTypes.length === 0 ? (
                        <div className="text-sm text-muted-foreground">No consideration types found.</div>
                    ) : (
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead className="min-w-[140px]">Space</TableHead>
                                    <TableHead className="min-w-[200px]">Consideration Type</TableHead>
                                    <TableHead className="min-w-[120px]">Criticality</TableHead>
                                    <TableHead className="min-w-[120px]">Status</TableHead>
                                    <TableHead className="min-w-[120px]">Subscribed</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {sortedTypes.map((type) => {
                                    return (
                                        <ConsiderationTypeRow
                                            key={type.id}
                                            considerationType={type}
                                            homeId={homeId}
                                            consideration={considerationByTypeId.get(type.id)}
                                            isSubscribed={subscribedTypeIds.has(type.id)}
                                            onlyShowOffered={onlyShowOffered}
                                            spaceMap={spaceMap}
                                            coverageToOffer={coverageToOffer}
                                        />
                                    );
                                })}
                            </TableBody>
                        </Table>
                    )}
                </CardContent>
            </Card>
        </div>
    );
};

const ConsiderationTypeRow: FC<{
    considerationType: ConsiderationType;
    homeId: HomeId;
    consideration: Consideration | undefined;
    isSubscribed: boolean;
    onlyShowOffered: boolean;
    spaceMap: Map<SpaceId, Space>;
    coverageToOffer: (type: ConsiderationType) => Promise<EquipmentId[] | undefined>;
}> = ({ considerationType, homeId, consideration, isSubscribed, onlyShowOffered, spaceMap, coverageToOffer }) => {
    const typeId = considerationType.id;
    const onRowClick = useRowClick();
    const [offering, setOffering] = useState(false);

    const spaceName = considerationType.spaceId
        ? (spaceMap.get(considerationType.spaceId)?.name ?? considerationType.spaceId)
        : null;

    const offerToHome = useCallback(
        async (e: React.MouseEvent) => {
            e.stopPropagation();
            setOffering(true);
            try {
                const considerationId = createId<ConsiderationId>();
                const now = new Date().toISOString();
                const coveredEquipmentIds = await coverageToOffer(considerationType);
                const payload: Consideration = {
                    object: 'consideration',
                    schema: 1,
                    id: considerationId,
                    homeId,
                    typeId: considerationType.id,
                    spaceId: considerationType.spaceId,
                    coveredEquipmentIds,
                    status: 'pending',
                    sourceType: 'manual',
                    criticality: considerationType.defaultCriticality,
                    ...(isActNowCriticality(considerationType.defaultCriticality)
                        ? { reviewAt: dateToLocalDate(new Date()) }
                        : {}),
                    createdAt: now,
                    updatedAt: now,
                };
                await considerationDao.setConsideration(homeId, considerationId, payload);
                log.info('Offered consideration to home', {
                    homeId,
                    typeId: considerationType.id,
                    considerationId,
                    coveredEquipmentIds,
                });
                toast.success('Consideration offered', {
                    description: `"${considerationType.title}" has been offered to this home.`,
                });
            } catch (err) {
                log.error('Failed to offer consideration to home', { err, homeId, typeId: considerationType.id });
                toast.error('Unable to offer consideration', {
                    description: err instanceof Error ? err.message : 'Unknown error',
                });
            } finally {
                setOffering(false);
            }
        },
        [homeId, considerationType, coverageToOffer],
    );

    // Filter out types without considerations if filter is on
    if (onlyShowOffered && !consideration) {
        return null;
    }

    return (
        <TableRow
            className="cursor-pointer transition-colors hover:bg-muted/50"
            onClick={onRowClick(`/directory/homes/${homeId}/considerations/${typeId}`)}
        >
            <TableCell>
                {spaceName ? (
                    <div className="text-sm">{spaceName}</div>
                ) : (
                    <div className="text-sm text-muted-foreground">—</div>
                )}
            </TableCell>
            <TableCell>
                <div className="font-medium">{considerationType.title}</div>
                <div className="text-xs text-muted-foreground">{considerationType.id}</div>
            </TableCell>
            <TableCell>
                <ConsiderationCriticalityBadge
                    criticality={consideration?.criticality ?? considerationType.defaultCriticality}
                />
            </TableCell>
            <TableCell>
                <div className="flex items-center gap-2">
                    <ConsiderationStatusBadge status={consideration?.status} variant="badge" />
                    {!consideration && (
                        <Button
                            variant="outline"
                            size="xs"
                            className="cursor-pointer"
                            disabled={offering}
                            onClick={offerToHome}
                        >
                            {offering ? 'Offering…' : 'Offer'}
                        </Button>
                    )}
                </div>
            </TableCell>
            <TableCell>
                <SubscriptionStatusBadge isSubscribed={isSubscribed} variant="text" />
            </TableCell>
        </TableRow>
    );
};

/**
 * What an offered consideration records (`offeredCoverage`), read at offer time. The atoms stay subscribed
 * while the tab is open, so the read is live.
 */
function useCoverageToOffer(): (type: ConsiderationType) => Promise<EquipmentId[] | undefined> {
    const store = useStore();
    useLoadableAtomValue(homeDocumentState);
    useLoadableAtomValue(homeHqlCatalogState);
    useLoadableAtomValue(taskTypesByConsiderationTypeState);
    return useCallback(
        async (type: ConsiderationType) => {
            const [doc, catalog, taskTypesByType] = await Promise.all([
                store.get(homeDocumentState),
                store.get(homeHqlCatalogState),
                store.get(taskTypesByConsiderationTypeState),
            ]);
            if (!doc) return undefined;
            return offeredCoverage({ type, taskTypes: taskTypesByType[type.id] ?? [], doc, catalog, now: new Date() });
        },
        [store],
    );
}
